import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping';
import DarkModeInfoWindow from '../components/DarkModeInfoWindow';
import SubLocationInfoWindow from '../components/SubLocationInfoWindow';
import '../components/maps/MapStyles.css';
import { MapControls } from '../components/maps/MapControls';
import {
  Box,
  Heading,
  Spinner,
  Text,
  Badge,
  useToast,
  useColorMode,
  useColorModeValue,
  HStack,
  Button,
  Icon,
  IconButton as ChakraIconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  Skeleton,
  Collapse,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image,
  useDisclosure
} from '@chakra-ui/react';
import {
  GoogleMap,
  InfoWindowF,
  MarkerClusterer,
  Marker,
} from '@react-google-maps/api';
import { Funnel, CaretUp, CaretDown, ArrowsOutSimple, ArrowsInSimple } from '@phosphor-icons/react';
import { CloseIcon } from '@chakra-ui/icons';
import ImagePreviewModal from '../components/ImagePreviewModal';
import SearchFilters from '../components/SearchFilters';
import SearchBox from '../components/SearchBox';
import MapLegend from '../components/MapLegend';
import SiteMarker from '../components/SiteMarker';
import SiteCard from '../components/SiteCard';
import SiteInfoWindow from '../components/SiteInfoWindow';
import { useMapData } from '../hooks/useMapData';
// import { useSiteData } from '../hooks/useSiteData'; // Potentially unused if we fetch directly
import { useGeocoding } from '../hooks/useGeocoding';
import { useFeatures } from '../hooks/useFeatures'; // Still used for features metadata in SiteCard
import { useAuth } from '../hooks/useAuth';
import { useMedia } from '../hooks/useMedia';
import { mediaHasFeature, normalizeFeature, mapFeature } from '../utils/featureUtils'; // Added mapFeature
import { getSearchableSites, getSiteSublocations } from '../firebase/searchableSiteQueries'; // Added for fetching sites and sublocations

// Update function to accept fullscreen state
const getMapContainerStyle = (colorMode, isFullscreen) => ({
  width: '100%',
  // Set height to 100% to fill the parent Box
  height: '100%', // Always 100% height of its container
  border: colorMode === 'dark' ? '1px solid #4A5568' : 'none',
  borderRadius: isFullscreen ? 0 : 'md', // No border radius in fullscreen
  overflow: 'hidden',
  // Add subtle texture based on color mode
  _after: {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    opacity: 0.08,
    bgImage: colorMode === 'light'
      ? 'var(--texture-canvas)' // Use canvas for map background
      : 'var(--texture-leather)', // Or leather for dark mode map
    bgRepeat: 'repeat',
    bgSize: '300px', // Adjust size as needed
    bgBlendMode: colorMode === 'light' ? 'soft-light' : 'multiply',
  }
});

// Updated mapStyles using theme tokens from visual_redesign_spec.md
const mapStyles = {
  light: [ // Styles for Light Mode ("Explorer's Journal")
    { elementType: 'geometry', stylers: [{ color: '#EBDEC2' }] }, // map.land: Sandy Parchment
    { elementType: 'labels.text.stroke', stylers: [{ color: '#F5F5F5' }] }, // Lighter stroke for better contrast
    { elementType: 'labels.text.fill', stylers: [{ color: '#3B2E1E' }] }, // map.text: Cartographer Ink
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#6A4E33' }] }, // text.link:  Brown (for emphasis)
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] }, // text.muted: Faded Ink
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#D9CAA7' }] }, // bg.accent: Subtle Tea Stain
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] }, // text.muted: Faded Ink
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FDF6E3' }] }, // Slightly lighter parchment
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#C0AD89' }] }, // border.light: Tea Edge
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] }, // text.muted: Faded Ink
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#F8EDD8' }] }, // Even lighter parchment
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#9C826B' }] }, // border.default: Coffee Stain
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#E6DAC0' }] }, // bg.surface: Faded Paper
    { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#6A4E33' }] }, // text.link:  Brown
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#A5C9D1' }] }, // A slightly desaturated blue for water
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3B2E1E' }] }, // map.text: Cartographer Ink
    { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#FFFFFF' }] }, // White stroke for water labels
  ],
  dark: [ // Styles for Dark Mode ("Night Explorer")
    { elementType: 'geometry', stylers: [{ color: '#38291E' }] }, // map.land: Dark Parchment
    { elementType: 'labels.text.stroke', stylers: [{ color: '#2C231B' }] }, // bg.default: Worn Leather
    { elementType: 'labels.text.fill', stylers: [{ color: '#D7D0C0' }] }, // map.text: Star Chart Silver
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#D6AD60' }] }, // text.link: Lantern Gold
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] }, // text.muted: Faded Journal
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#483123' }] }, // bg.accent: Deep Leather
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] }, // text.muted: Faded Journal
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#483123' }] }, // bg.accent: Deep Leather
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#50392A' }] }, // border.heavy: Dark Binding
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] }, // text.muted: Faded Journal
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#50392A' }] }, // border.heavy: Dark Binding
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#6A4E33' }] }, // border.default: Aged Leather
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#483123' }] }, // bg.accent: Deep Leather
    { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#D6AD60' }] }, // text.link: Lantern Gold
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#233947' }] }, // map.water: Midnight Sea
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#D7D0C0' }] }, // map.text: Star Chart Silver
    { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#2C231B' }] }, // bg.default: Worn Leather
  ],
};

const MapPage = () => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedEra, setSelectedEra] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('');

  // New state for direct site fetching and management
  const [initialSites, setInitialSites] = useState([]);
  const [displayedSites, setDisplayedSites] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [featuresMetadata, setFeaturesMetadata] = useState({}); // For getFeatureLabel and SearchFilters
  const [availableCountries, setAvailableCountries] = useState([]); // For SearchFilters
  
  const {
    // sites: filteredSites, // Will be replaced by displayedSites
    // loading, // Will be replaced by isInitialLoading
    center,
    setCenter,
    activeMarkerId,
    setActiveMarkerId,
    // setFilters: setHookFilters, // Filtering will be handled locally
  } = useMapData();
  
  const { features } = useFeatures(); // Still used for SiteCard's featuresMetadata prop
  const { user } = useAuth();
  const { reverseGeocode } = useGeocoding();
  const { colorMode } = useColorMode();
  const { loadMedia } = useMedia();
  
  const searchBoxRef = useRef(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showMainSites, setShowMainSites] = useState(true); // State for main site visibility
  const [showSublocations, setShowSublocations] = useState(false);
  // const [isApplyingFilters, setIsApplyingFilters] = useState(false); // Will use 'loading' from useMapData
  const [isCustomFullscreen, setIsCustomFullscreen] = useState(false); // State for custom fullscreen
  const [mapInstance, setMapInstance] = useState(null); // State to hold map instance
  const [mapTypeId, setMapTypeId] = useState('terrain'); // Default to terrain map type
  // const filterTimeoutRef = useRef(null); // Removed as the useEffect using it is removed

  // Local state for filters in Map.jsx, used to control UI and inform useMapData
  const [componentFilters, setComponentFilters] = useState({
    materialType: '',
    features: [],
    era: '',
    verificationStatus: '',
    countries: [],
    featureFilterLogic: 'AND' // Added from Search.jsx
  });

  const handleFilterChange = (newFilters) => {
    setComponentFilters(prev => ({ ...prev, ...newFilters }));
    // setHookFilters is no longer called here; filtering is handled by a new useEffect
  };

  const resetFilters = () => {
    const freshFilters = {
      materialType: '',
      features: [],
      era: '',
      verificationStatus: '',
      countries: [],
      featureFilterLogic: 'AND' // Reset this too
    };
    setComponentFilters(freshFilters);
    // setHookFilters is no longer called here
  };

  // Helper functions copied/adapted from Search.jsx
  // Placed here for encapsulation within MapPage component

  // New helper: Checks if a media item contains a specific standardized feature
  const mediaContainsStandardizedFeature = useCallback((mediaItem, standardFeatureId) => {
    if (!mediaItem?.predictions || !Array.isArray(mediaItem.predictions) || !standardFeatureId) return false;
    
    return mediaItem.predictions.some(pred => {
      if (!pred || !pred.class) return false;
      // In Search.jsx, there's a confidence check for 'auto' source.
      // Assuming mapFeature handles standardization correctly.
      // if (pred.source === 'auto' && pred.confidence < 0.05) return false;

      const { featureId: mappedPredFeatureId } = mapFeature(pred);
      return mappedPredFeatureId === standardFeatureId;
    });
  }, []); // mapFeature should be stable if imported correctly
  
  // Check if a site has a feature (uses standardized IDs)
  const siteHasFeature = useCallback((site, standardFeatureId) => {
    // For searchable sites, use siteLevelAllImageFeatureTags for efficient checking
    if (Array.isArray(site.siteLevelAllImageFeatureTags)) {
      return site.siteLevelAllImageFeatureTags.includes(standardFeatureId);
    }
    // Fallback to legacy media array if available (adapted from Search.jsx)
    // This part might need adjustment based on how media is structured in `initialSites`
    // For now, assuming `site.media` might exist and contain predictions.
    return Array.isArray(site.media) && site.media.some(mediaItem => mediaContainsStandardizedFeature(mediaItem, standardFeatureId));
  }, [mediaContainsStandardizedFeature]);

  // Helper function to check if a date matches an era (copied from Search.jsx)
  const dateMatchesEra = (date, era) => {
    if (!date) return false;
    const num = parseInt(date);
    if (isNaN(num)) return false;
    
    const isBCE = date.toLowerCase().includes('bce');
    const year = isBCE ? -num : num;
    
    const eraRanges = {
      prehistoric: [-100000, -3000],
      ancient: [-3000, 500],
      classical: [-500, 500],
      medieval: [500, 1500],
      modern: [1500, 2100]
    };

    const range = eraRanges[era];
    return range && year >= range[0] && year <= range[1];
  };

  // Helper function to check if site's dates match era (copied from Search.jsx)
  const siteMatchesEra = useCallback((site, era) => {
    if (!era) return true;
    if (site.constructionPeriods?.length > 0) {
      return site.constructionPeriods.some(period => {
        const startMatch = dateMatchesEra(period.startEra, era);
        const endMatch = dateMatchesEra(period.endEra, era);
        return startMatch || endMatch;
      });
    }
    return dateMatchesEra(site.startEra, era) || dateMatchesEra(site.endEra, era);
  }, []);

  // Filter sites based on criteria (copied and adapted from Search.jsx)
  const applyFilters = useCallback((sitesToFilter, currentFilters) => {
    if (!Array.isArray(sitesToFilter)) return [];
    return sitesToFilter.filter(site => {
      if (currentFilters.features?.length > 0) {
        if (currentFilters.featureFilterLogic === 'OR') {
          const siteMatchesAnyFeature = currentFilters.features.some(stdId => siteHasFeature(site, stdId));
          if (!siteMatchesAnyFeature) return false;
        } else { // AND (default)
          const siteMatchesAllFeatures = currentFilters.features.every(stdId => siteHasFeature(site, stdId));
          if (!siteMatchesAllFeatures) return false;
        }
      }
      if (currentFilters.era && !siteMatchesEra(site, currentFilters.era)) return false;
      // verificationStatus filter is not in Search.jsx's active filters, so skipping here too for now.
      // if (currentFilters.verificationStatus && site.verificationStatus !== currentFilters.verificationStatus) return false;
      if (currentFilters.countries?.length > 0) {
        const siteCountry = site.country || site.location?.country;
        if (!siteCountry || !currentFilters.countries.includes(siteCountry)) return false;
      }
      // materialType is not in Search.jsx's applyFilters, skipping for now.
      return true;
    });
  }, [siteHasFeature, siteMatchesEra]);

  // Get feature label using standardized ID from featuresMetadata (copied from Search.jsx)
  const getFeatureLabel = useCallback((standardFeatureId) => {
    if (featuresMetadata && featuresMetadata[standardFeatureId]) {
      return featuresMetadata[standardFeatureId].label || standardFeatureId;
    }
    return standardFeatureId;
  }, [featuresMetadata]);


  // useEffect for initial data fetching
  useEffect(() => {
    const fetchInitialSites = async () => {
      setIsInitialLoading(true);
      setFetchError(null);
      try {
        console.log('[Map.jsx] Fetching initial sites...');
        const result = await getSearchableSites();
        console.log('[Map.jsx] getSearchableSites result:', result);

        if (result.success) {
          console.log('[Map.jsx] Successfully fetched sites. Count:', result.data.length);
          const sitesWithSublocations = await Promise.all(
            result.data.map(async (site) => {
              const sublocationsResult = await getSiteSublocations(site.id);
              return {
                ...site,
                sublocations: sublocationsResult.success ? sublocationsResult.data : [],
              };
            })
          );
          setInitialSites(sitesWithSublocations);
          // setDisplayedSites(sitesWithSublocations); // This will be handled by the filtering useEffect

          // Populate featuresMetadata and availableCountries (logic from Search.jsx)
          console.log('[Map.jsx] Populating featuresMetadata and availableCountries...');
          const featureMap = new Map();
          result.data.forEach(site => {
            if (Array.isArray(site.siteLevelAllImageFeatureTags)) {
              site.siteLevelAllImageFeatureTags.forEach(rawFeatureId => {
                const { featureId: standardId, label, category } = mapFeature({ class: rawFeatureId, source: 'system', confidence: 1.0 });
                if (standardId && !featureMap.has(standardId)) {
                  featureMap.set(standardId, { id: standardId, label: label || standardId, category: category || 'uncategorized', source: 'system' });
                }
              });
            }
            if (Array.isArray(site.searchableMediaItems)) {
              site.searchableMediaItems.forEach(mediaItem => {
                if (Array.isArray(mediaItem.featureTags)) {
                  mediaItem.featureTags.forEach(rawFeatureId => {
                    const { featureId: standardId, label, category } = mapFeature({ class: rawFeatureId, source: 'system', confidence: 1.0 });
                    if (standardId && !featureMap.has(standardId)) {
                      featureMap.set(standardId, { id: standardId, label: label || standardId, category: category || 'uncategorized', source: 'system' });
                    }
                  });
                }
              });
            }
          });
          setFeaturesMetadata(Object.fromEntries(featureMap));
          
          const countriesFromSites = result.data.map(site => site.country || site.location?.country);
          const uniqueCountries = [...new Set(countriesFromSites.filter(Boolean).map(c => c.trim()).filter(c => c !== ''))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          setAvailableCountries(uniqueCountries);
          console.log('[Map.jsx] FeaturesMetadata and availableCountries populated.');

        } else {
          console.error('[Map.jsx] Failed to fetch sites:', result.error);
          setFetchError(result.error || 'Failed to fetch sites');
          setInitialSites([]);
          setAvailableCountries([]);
          setFeaturesMetadata({});
        }
      } catch (err) {
        console.error('[Map.jsx] Error in fetchInitialSites:', err);
        setFetchError('An error occurred while fetching sites');
        setInitialSites([]);
        setAvailableCountries([]);
        setFeaturesMetadata({});
      }
      setIsInitialLoading(false);
    };
    fetchInitialSites();
  }, []); // Runs once on mount

  // useEffect for filtering sites when initialSites or componentFilters change
  useEffect(() => {
    if (!isInitialLoading && initialSites.length > 0) {
      const filtered = applyFilters(initialSites, componentFilters);
      setDisplayedSites(filtered);
    } else if (!isInitialLoading && initialSites.length === 0) {
      setDisplayedSites([]); // Handle case where initial fetch yields no sites
    }
    // If initialSites is empty due to error or no data, displayedSites should also be empty.
    // This effect depends on isInitialLoading to ensure it runs after fetch.
  }, [initialSites, componentFilters, applyFilters, isInitialLoading]);


  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false, // Disable default map type control
    fullscreenControl: false, // Disable native fullscreen button
    styles: colorMode === 'dark' ? mapStyles.dark : mapStyles.light,
  }), [colorMode]);

  const handlePlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const location = places[0].geometry.location;
      const newCenter = {
        lat: location.lat(),
        lng: location.lng(),
      };
      setCenter(newCenter);
    }
  }, [setCenter]);

  const onSearchBoxLoad = useCallback((ref) => {
    searchBoxRef.current = ref;
  }, []);

  const handleMarkerClick = useCallback(async (site) => {
    if (!site?.id) return;
    
    const mediaResult = await loadMedia(site.id);
    setSelectedSite({
      ...site,
      media: mediaResult?.data || []
    });
  }, [loadMedia]);

  // Map type change handler
  const handleMapTypeChange = useCallback(() => {
    setMapTypeId(prev => {
      if (prev === 'terrain') return 'hybrid';
      if (prev === 'hybrid') return 'satellite';
      return 'terrain';
    });
  }, []);

  // Labels toggle handler
  const handleToggleLabels = useCallback(() => {
    setShowLabels(prev => !prev);
  }, []);

  // Fullscreen toggle handler
  const handleToggleFullscreen = useCallback(() => {
    setIsCustomFullscreen(prev => !prev);
  }, []);

  const hasFeature = useCallback((media, featureId) => {
    // This function is used by getFilteredMedia, which filters media *within* a SiteCard.
    // It's not used for filtering sites on the map anymore.
    return mediaHasFeature(media, featureId);
  }, []);

  // The main useEffect for applying filters to the site list is removed.
  // Filtering is now primarily handled by useMapData based on `componentFilters` passed to `setHookFilters`.
  // `filteredSites` from `useMapData` is used directly for rendering.

  // Effect to close InfoWindow if the selected site is no longer in the filtered list
  useEffect(() => {
    if (selectedSite && !isInitialLoading && displayedSites && !displayedSites.find(site => site.id === selectedSite.id)) {
      setSelectedSite(null);
    }
  }, [selectedSite, displayedSites, isInitialLoading]);
  
  // Effect to trigger map resize on fullscreen change
  useEffect(() => {
    if (mapInstance) {
      // Trigger resize after a short delay to allow DOM changes to settle
      const timer = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance, 'resize');
        // Recenter map after resize
        mapInstance.setCenter(center);
      }, 100); // Adjust delay if needed
      return () => clearTimeout(timer);
    }
  }, [isCustomFullscreen, mapInstance, center]); // Rerun when fullscreen, map instance, or center changes

  const getFilteredMedia = useCallback((allMedia) => {
    try {
      // This function filters media items for display within a SiteCard, based on componentFilters.
      if (!componentFilters.features?.length || !Array.isArray(allMedia)) {
        return allMedia || []; // If allMedia is undefined/null, returns []
      }
      
      const result = allMedia.filter(media => 
        media && componentFilters.features.every(featureId => hasFeature(media, featureId))
      );
      return result || []; // Ensure an array is returned
    } catch (error) {
      console.error("Error in getFilteredMedia:", error, {
        allMedia,
        componentFiltersFeatures: componentFilters?.features,
      });
      return []; // Return an empty array in case of any error
    }
  }, [componentFilters.features, hasFeature]);

  const handleMediaClick = useCallback((mediaItem, index) => {
    // Ensure selectedSite and its media are available
    if (!selectedSite || !selectedSite.media) return;
    const activeMedia = getFilteredMedia(selectedSite.media);
    setSelectedImage(mediaItem);
    setSelectedMediaIndex(index);
    onOpen();
  }, [getFilteredMedia, selectedSite]);

  const handleNavigate = useCallback((direction) => {
    const activeMedia = getFilteredMedia(selectedSite?.media);
    if (!activeMedia.length) return;

    let newIndex;
    if (direction === 'next') {
      newIndex = selectedMediaIndex >= activeMedia.length - 1 ? 0 : selectedMediaIndex + 1;
    } else {
      newIndex = selectedMediaIndex <= 0 ? activeMedia.length - 1 : selectedMediaIndex - 1;
    }

    setSelectedMediaIndex(newIndex);
    setSelectedImage(activeMedia[newIndex]);
  }, [selectedSite, selectedMediaIndex, getFilteredMedia]);

  const handleMapClick = useCallback(() => {
    setSelectedSite(null);
  }, []);

  // Update useMemo to include isCustomFullscreen and pass it to the style function
  const mapContainerStyle = useMemo(() => getMapContainerStyle(colorMode, isCustomFullscreen), [colorMode, isCustomFullscreen]);

  const MemoizedMarkers = React.memo(({ sites, onClick, showLabels, isMainMap, showMainSites }) => {
    // Filter sites based on showMainSites state before mapping
    const visibleSites = showMainSites ? sites : [];
    
    return visibleSites.map((site) => (
      <SiteMarker
        key={site.id}
        site={site}
        onClick={onClick}
        clusterer={null}
        showLabels={showLabels}
        isMainMap={isMainMap}
      />
    ));
  });

  return (
    // Apply fullscreen styles and theme background to the root Box
    <Box
      p={isCustomFullscreen ? 0 : 4} // No padding in fullscreen
      bg={useColorModeValue('bg.default', 'bg.default')} // Use theme background
      minH="100vh"
      position={isCustomFullscreen ? 'fixed' : 'relative'}
      top={isCustomFullscreen ? 0 : 'auto'}
      left={isCustomFullscreen ? 0 : 'auto'}
      width={isCustomFullscreen ? '100vw' : '100%'}
      height={isCustomFullscreen ? '100vh' : 'auto'}
      zIndex={isCustomFullscreen ? 1000 : 'auto'}
      overflow={isCustomFullscreen ? 'hidden' : 'visible'} // Hide overflow in fullscreen
      display="flex" // Use flexbox for layout
      flexDirection="column" // Stack children vertically
    >
      {/* Hide Heading and other elements above map in fullscreen */}
      {!isCustomFullscreen && (
        <>
          {/* Apply theme font and color */}
          <Heading mb={4} color="text.default" fontFamily="heading" size="xl">
            Megalithic Sites Explorer
          </Heading>
          <Box mb={4}>
            <HStack mb={2} justify="space-between" width="100%">
              {/* Use themed button variant */}
              <Button
                leftIcon={showFilters ? <CaretUp /> : <CaretDown />}
                onClick={() => setShowFilters(!showFilters)}
                size="sm"
                variant="journal" // Use journal variant
                // Dynamically adjust appearance based on active filters
                bg={componentFilters.features.length > 0 ? useColorModeValue('interactive.primary', 'interactive.primary') : useColorModeValue('bg.surface', 'bg.surface')}
                color={componentFilters.features.length > 0 ? 'white' : 'text.default'}
                borderColor={componentFilters.features.length > 0 ? 'interactive.primary' : 'border.default'}
                _hover={{
                  bg: componentFilters.features.length > 0 ? useColorModeValue('interactive.hover', 'interactive.hover') : useColorModeValue('bg.accent', 'bg.accent'),
                }}
              >
                Filters {componentFilters.features.length > 0 && `(${componentFilters.features.length})`}
                {componentFilters.countries?.length > 0 && `, Countries (${componentFilters.countries.length})`}
                {componentFilters.materialType && `, ${componentFilters.materialType}`}
                {componentFilters.era && `, ${componentFilters.era}`}
                 {/* Display feature filter logic if features are selected */}
                 {componentFilters.features && componentFilters.features.length > 0 && ` (${componentFilters.featureFilterLogic})`}
              </Button>
              {/* Buttons moved to map controls */}
            </HStack>

            <Collapse in={showFilters} animateOpacity>
              {/* Apply theme styles to filter container */}
              <Box
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="lg"
                bg="bg.surface" // Use surface background
                p={4} // Add padding
                boxShadow="sm"
                position="relative"
                _before={{ // Add subtle texture
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  opacity: 0.05,
                  pointerEvents: 'none',
                  bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
                  bgRepeat: 'repeat',
                  bgSize: '300px',
                  borderRadius: 'inherit',
                }}
              >
                <SearchFilters
                  filters={{...componentFilters, featuresMetadata}} // Pass featuresMetadata
                  onFilterChange={handleFilterChange}
                  onReset={resetFilters}
                  variant="flat"
                  // Pass available countries for the country filter dropdown
                  filterOptions={{ countries: availableCountries.map(country => ({ id: country, label: country })) }}
                  // enableElasticsearchAggregations={false} // This prop is from Search.jsx, ensure SearchFilters handles its absence or set to false
                />
              </Box>
            </Collapse>
          </Box>

          <HStack mb={4} wrap="wrap" spacing={2}>
            {/* Use themed text color */}
            <Text color="text.muted" fontSize="sm">
              Showing {displayedSites.length} sites {fetchError && <Badge colorScheme="red">Error loading</Badge>}
            </Text>
          </HStack>

          <SearchBox onPlacesChanged={handlePlacesChanged} onLoad={onSearchBoxLoad} />
        </>
      )}

      {/* Map Container Box - Apply theme styles */}
      <Box
        bg={useColorModeValue('map.land', 'map.land')} // Use map land color
        p={isCustomFullscreen ? 0 : 4} // No padding in fullscreen
        borderRadius={isCustomFullscreen ? 0 : "lg"} // No border radius in fullscreen
        boxShadow={isCustomFullscreen ? "none" : "lg"} // Use larger shadow for depth
        borderWidth={isCustomFullscreen ? 0 : "1px"} // Add border
        borderColor="border.heavy" // Use heavy border
        position="relative" // Keep relative for SiteCard positioning
        flexGrow={1} // Allow this box to grow and fill remaining space
        width="100%" // Ensure it takes full width
        height={isCustomFullscreen ? '100%' : '80vh'} // Restore 80vh for non-fullscreen
      >
        {isInitialLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Spinner size="xl" color={colorMode === 'dark' ? 'ancient.papyrus' : 'ancient.terracotta'} />
          </Box>
        ) : fetchError ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" textAlign="center">
            <Text color="red.500">Error: {fetchError}</Text>
          </Box>
        ) : (
          // Ensure this inner Box also takes full height
          <Box position="relative" h="100%">
            {/* Removed the "Applying filters..." overlay as client-side filtering should be fast */}

            {/* Use new React-based controls */}
            <MapControls
              mapTypeId={mapTypeId}
              onMapTypeChange={handleMapTypeChange}
              showLabels={showLabels}
              onToggleLabels={handleToggleLabels}
              isFullscreen={isCustomFullscreen}
              onToggleFullscreen={handleToggleFullscreen}
            />

            <GoogleMap
              mapContainerStyle={mapContainerStyle} // Style now controls height based on parent
              center={center}
              zoom={4}
              options={mapOptions}
              mapTypeId={mapTypeId} // Control map type via state
              onLoad={setMapInstance} // Store map instance on load
              onClick={handleMapClick}
            >
              <MarkerClusterer
                options={{
                  imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
                  maxZoom: 15,
                  gridSize: 60,
                  styles: [{
                    url: colorMode === 'dark' ? '/cluster-dark.png' : '/cluster-light.png',
                    height: 53,
                    width: 53,
                    textColor: colorMode === 'dark' ? '#FFFFFF' : '#000000',
                    textSize: 12
                  }]
                }}
              >
                {(clusterer) => (
                  <MemoizedMarkers
                    sites={displayedSites} // Use displayedSites
                    onClick={handleMarkerClick}
                    clusterer={clusterer}
                    showLabels={showLabels}
                    isMainMap={true}
                    showMainSites={showMainSites} // Pass state here
                  />
                )}
              </MarkerClusterer>

              {showSublocations && displayedSites.map(site => { // Use displayedSites
                // const siteMedia = mediaCache.get(site.id) || []; // mediaCache likely removed or changed
                // const filteredSiteMedia = getFilteredMedia(siteMedia); // Relies on componentFilters
                
                return site.sublocations?.map(sublocation => {
                  // Sublocation rendering logic might need further review based on how sublocation
                  // data is fetched and filtered, especially if it needs to align with componentFilters.
                  // For now, keeping the existing click handler that loads media.
                  // If sublocations should be filtered out based on parent site's media not matching
                  // componentFilters.features, that logic is implicitly handled by `displayedSites`
                  // only containing matching parent sites.

                  return (
                    <React.Fragment key={`${site.id}-${sublocation.id}`}>
                      <Marker
                        position={sublocation.coordinates}
                        icon={{
                          url: '/marker-sub.svg',
                          scaledSize: new window.google.maps.Size(24, 36)
                        }}
                        onClick={async () => {
                          if (!site?.id) return;
                          
                          const mediaResult = await loadMedia(site.id);
                          const allSiteMedia = mediaResult?.success ? mediaResult.data : [];
                          const sublocationMediaItems = allSiteMedia.filter(
                            item => item.subLocationId === sublocation.id
                          );
                          
                          setSelectedSite({
                            ...site,
                            selectedSublocation: sublocation,
                            media: sublocationMediaItems
                          });
                        }}
                      />
                    </React.Fragment>
                  );
                });
              })}

              {selectedSite && (
                <Box
                  position="absolute" // Position remains absolute relative to the map container
                  top={4} // Consistent top spacing (theme value 4)
                  right={4} // Consistent right spacing (theme value 4)
                  zIndex={10} // Keep above map markers
                  maxWidth="400px"
                  width="100%"
                >
                  <Box position="relative">
                    <ChakraIconButton
                      position="absolute"
                      right={2}
                      top={2}
                      zIndex={11} // Above SiteCard
                      aria-label="Close"
                      icon={<Icon as={CloseIcon} />}
                      onClick={() => setSelectedSite(null)}
                      size="sm"
                      variant="ghost" // Use ghost for less emphasis
                      color="text.muted" // Muted color
                      _hover={{ bg: useColorModeValue('blackAlpha.100', 'whiteAlpha.100'), color: 'text.default' }}
                    />
                    {/* Ensure SiteCard uses journal variant */}
                    <SiteCard
                      site={selectedSite}
                      variant="journal" // Explicitly set variant if needed, or ensure theme default is journal
                      showFilteredMedia={componentFilters.features?.length > 0}
                      filteredMedia={getFilteredMedia(selectedSite.media)} // Uses componentFilters
                      systemFilteredMedia={getFilteredMedia(selectedSite.media)} // Uses componentFilters
                      onFeatureFilter={(feature) => {
                        // Extract raw ID from the feature object or use the string directly
                        const rawId = typeof feature === 'string' ? feature : feature.id;
                        
                        // Normalize for comparison only
                        const normalizedId = normalizeFeature(rawId);
                        
                        // Check if the normalized feature ID is already in the componentFilters
                        const exists = componentFilters.features.some(id => normalizeFeature(id) === normalizedId);
                        
                        // Update componentFilters, preserving original format for added features
                        let newFeatures;
                        if (exists) {
                          newFeatures = componentFilters.features.filter(id => normalizeFeature(id) !== normalizedId);
                        } else {
                          newFeatures = [...componentFilters.features, rawId];
                        }
                        
                        // Log for debugging
                        console.log(`Map: Toggle feature: ${rawId} (normalized: ${normalizedId}), exists: ${exists}, new features:`, newFeatures);
                          
                        handleFilterChange({ features: newFeatures }); // This will update both local and hook filters
                      }}
                      filters={{...componentFilters, featuresMetadata: features}} // Pass componentFilters to SiteCard
                      onMediaClick={handleMediaClick}
                      selectedSublocation={selectedSite.selectedSublocation}
                    />
                  </Box>
                </Box>
              )}
            </GoogleMap>
            
            {/* Keep MapLegend inside the map container but outside GoogleMap */}
            <MapLegend
              showMainSites={showMainSites}
              onToggleMainSites={() => setShowMainSites(!showMainSites)}
              showSublocations={showSublocations}
              onToggleSublocations={() => setShowSublocations(!showSublocations)}
              // Position legend at bottom-left, adjust as needed
              position="absolute" 
              bottom={4} 
              left={4} 
              zIndex={5} // Ensure it's above map tiles but below SiteCard
            />
          </Box>
        )}
      </Box>

      {/* Keep ImagePreviewModal outside the main fullscreen container */}
      <ImagePreviewModal
        isOpen={isOpen}
        onClose={onClose}
        imageUrl={selectedImage?.url}
        onPrevious={() => handleNavigate('prev')}
        onNext={() => handleNavigate('next')}
        hasMultiple={getFilteredMedia(selectedSite?.media)?.length > 1}
        currentImage={selectedImage}
      />
    </Box>
  );
};

export default React.memo(MapPage);
